import { useState, useCallback, useMemo, useEffect } from 'react';
import { DEFAULT_PAGE_SIZE, type PaginationParams } from '@/types/pagination';

interface UsePaginationOptions {
  defaultPageSize?: number;
  persistInUrl?: boolean;
  /**
   * When provided, the current page and page size are persisted to
   * sessionStorage under this key, so they survive navigation away from and
   * back to the grid. Takes precedence over the default page/pageSize when a
   * stored value exists.
   */
  storageKey?: string;
}

interface StoredPagination {
  page?: number;
  pageSize?: number;
}

export function usePagination(options: UsePaginationOptions = {}) {
  const { defaultPageSize = DEFAULT_PAGE_SIZE, persistInUrl = true, storageKey } = options;

  const readStored = (): StoredPagination => {
    if (!storageKey || typeof window === 'undefined') return {};
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as StoredPagination;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  // Get initial values from sessionStorage, URL or defaults
  const getInitialPage = () => {
    if (storageKey) {
      const stored = readStored();
      if (stored.page && stored.page > 0) return stored.page;
    }
    if (persistInUrl && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return Number(params.get('page')) || 1;
    }
    return 1;
  };
  
  const getInitialPageSize = () => {
    if (storageKey) {
      const stored = readStored();
      if (stored.pageSize && stored.pageSize > 0) return stored.pageSize;
    }
    if (persistInUrl && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return Number(params.get('pageSize')) || defaultPageSize;
    }
    return defaultPageSize;
  };
  
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [pageSize, setCurrentPageSize] = useState(getInitialPageSize);
  
  // Update URL when page or pageSize changes
  useEffect(() => {
    if (persistInUrl && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.set('page', String(currentPage));
      params.set('pageSize', String(pageSize));
      
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [currentPage, pageSize, persistInUrl]);

  // Persist page/pageSize to sessionStorage when they change
  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify({ page: currentPage, pageSize }),
        );
      } catch {
        // Ignore storage failures (e.g. private mode / quota)
      }
    }
  }, [currentPage, pageSize, storageKey]);
  
  const setPage = useCallback((page: number | ((prev: number) => number)) => {
    setCurrentPage((prev) => {
      const newPage = typeof page === 'function' ? page(prev) : page;
      return newPage;
    });
  }, []);
  
  const setPageSize = useCallback((size: number) => {
    setCurrentPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);
  
  const goToFirstPage = useCallback(() => setPage(1), [setPage]);
  const goToLastPage = useCallback((totalPages: number) => setPage(totalPages), [setPage]);
  const goToNextPage = useCallback(() => setPage(p => p + 1), [setPage]);
  const goToPreviousPage = useCallback(() => setPage(p => Math.max(1, p - 1)), [setPage]);
  
  const resetPagination = useCallback(() => {
    setPage(1);
  }, [setPage]);
  
  const paginationParams: PaginationParams = useMemo(() => ({
    page: currentPage,
    pageSize,
  }), [currentPage, pageSize]);
  
  return {
    currentPage,
    pageSize,
    paginationParams,
    setPage,
    setPageSize,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
  };
}
